import React, { useState, useEffect } from 'react';
import { useDomain } from '../contexts/DomainContext';
import { getConnectedAccounts, getGoogleDashboardSummary, getGoogleProperties } from '../services/integrationsService';

const GoogleInsightsPanel = () => {
  const { selectedDomain } = useDomain();
  const [loading, setLoading] = useState(true);
  const [googleAccount, setGoogleAccount] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState(null);
  const [siteUrl, setSiteUrl] = useState(null);
  const [ga4PropertyId, setGa4PropertyId] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const acctRes = await getConnectedAccounts();
        const accounts = acctRes.data?.accounts || [];
        const ga = accounts.find(a => a.provider === 'google' && a.status === 'active');
        if (!ga) { setLoading(false); return; }
        setGoogleAccount(ga);

      // Fetch properties to get siteUrl and ga4PropertyId
      const propRes = await getGoogleProperties(ga.id);
      const props = propRes.data;
      const sites = props.searchConsoleSites || [];
      const properties = props.properties || [];

      // Get current domain from context
      const currentDomain = selectedDomain ? selectedDomain.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase() : null;

      // Check for saved settings in account metadata
      let savedSite = null;
      let savedPropId = null;
      if (ga.metadata) {
        try {
          const meta = typeof ga.metadata === 'string' ? JSON.parse(ga.metadata) : ga.metadata;
          if (currentDomain && meta?.domainSettings?.[currentDomain]) {
            savedSite = meta.domainSettings[currentDomain].gscSiteUrl;
            savedPropId = meta.domainSettings[currentDomain].ga4PropertyId;
          }
        } catch (e) {}
      }

      // Determine Search Console site: saved > domain match > first
      let site = savedSite || null;
      if (!site && currentDomain) {
        const matched = sites.find(s => {
          const siteClean = s.siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase();
          return siteClean === currentDomain || currentDomain.includes(siteClean) || siteClean.includes(currentDomain);
        });
        if (matched) site = matched.siteUrl;
      }
      if (!site && sites.length > 0) site = sites[0].siteUrl;

      // Determine GA4 property: saved > first
      const propId = savedPropId || (properties.length > 0 ? properties[0].propertyId : null);

      setSiteUrl(site);
      setGa4PropertyId(propId);

        if (site || propId) {
          const body = {};
          if (site) body.siteUrl = site;
          if (propId) body.propertyId = propId;
          const summRes = await getGoogleDashboardSummary(ga.id, body);
          setSummaryData(summRes.data);
        }
      } catch (e) {
        console.error('Google insights error:', e);
        setError('Unable to load Google data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [selectedDomain]);

  const fmtNum = (n) => {
    if (n === undefined || n === null) return '';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return Number(n).toLocaleString();
  };

  const fmtPct = (n) => {
    if (n === undefined || n === null) return '';
    return (Number(n) * 100).toFixed(1) + '%';
  };

  const fmtPos = (n) => {
    if (n === undefined || n === null) return '';
    return Number(n).toFixed(1);
  };

  const calcChange = (curr, prev) => {
    if (!curr || !prev || prev === 0) return null;
    return ((curr - prev) / prev * 100).toFixed(1);
  };

  const ChangeIndicator = ({ current, previous }) => {
    const change = calcChange(current, previous);
    if (change === null) return <span className="text-xs text-gray-400"></span>;
    const isPos = parseFloat(change) >= 0;
    return (
      <span className={`text-xs font-medium ${isPos ? 'text-green-600' : 'text-red-500'}`}>
        {isPos ? '' : ''} {Math.abs(parseFloat(change))}%
      </span>
    );
  };

  // Mini sparkline using simple SVG
  const MiniChart = ({ data, dataKey, color }) => {
    if (!data || data.length === 0) return null;
    const values = data.map(d => d[dataKey] || 0);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const w = 120;
    const h = 32;
    const pts = values.map((v, i) => {
      const x = (i / Math.max(values.length - 1, 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      return `${x},${y}`;
    }).join(' ');
    return (
      <svg width={w} height={h} className="mt-1">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="h-5 bg-gray-200 rounded w-48"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!googleAccount) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 mb-6 border border-blue-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Connect Google to unlock insights</h3>
              <p className="text-sm text-gray-500">See your Search Console & Analytics data right on your dashboard</p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/integrations'}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Connect Google
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg shadow-md p-6 mb-6 border border-red-100">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (!summaryData) return null;

  const gsc = summaryData.gsc;
  const ga4 = summaryData.ga4;

  return (
    <div className="mb-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <h2 className="text-lg font-semibold text-gray-800">Google Insights</h2>
          <span className="text-xs text-gray-400 ml-1">Last 28 days</span>
        </div>
        <span className="text-xs text-gray-400">{googleAccount.providerEmail}</span>
      </div>

      {/* GSC Section */}
      {gsc && gsc.current && (
        <div className="bg-white rounded-lg shadow-md p-5">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <h3 className="text-sm font-semibold text-gray-700">Search Console</h3>
            {siteUrl && <span className="text-xs text-gray-400">{siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Clicks */}
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Clicks</div>
              <div className="text-2xl font-bold text-blue-700">{fmtNum(gsc.current.clicks)}</div>
              <div className="flex items-center justify-between mt-1">
                <ChangeIndicator current={gsc.current.clicks} previous={gsc.previous?.clicks} />
                <MiniChart data={gsc.dailyTrend} dataKey="clicks" color="#3B82F6" />
              </div>
            </div>
            {/* Impressions */}
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Impressions</div>
              <div className="text-2xl font-bold text-purple-700">{fmtNum(gsc.current.impressions)}</div>
              <div className="flex items-center justify-between mt-1">
                <ChangeIndicator current={gsc.current.impressions} previous={gsc.previous?.impressions} />
                <MiniChart data={gsc.dailyTrend} dataKey="impressions" color="#8B5CF6" />
              </div>
            </div>
            {/* CTR */}
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Avg CTR</div>
              <div className="text-2xl font-bold text-green-700">{fmtPct(gsc.current.ctr)}</div>
              <div className="flex items-center justify-between mt-1">
                <ChangeIndicator current={gsc.current.ctr} previous={gsc.previous?.ctr} />
                <MiniChart data={gsc.dailyTrend} dataKey="ctr" color="#10B981" />
              </div>
            </div>
            {/* Position */}
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Avg Position</div>
              <div className="text-2xl font-bold text-amber-700">{fmtPos(gsc.current.position)}</div>
              <div className="flex items-center justify-between mt-1">
                {gsc.previous?.position ? (
                  <span className={`text-xs font-medium ${gsc.current.position <= gsc.previous.position ? 'text-green-600' : 'text-red-500'}`}>
                    {gsc.current.position <= gsc.previous.position ? '' : ''} {Math.abs(gsc.current.position - gsc.previous.position).toFixed(1)}
                  </span>
                ) : <span className="text-xs text-gray-400"></span>}
                <MiniChart data={gsc.dailyTrend} dataKey="position" color="#F59E0B" />
              </div>
            </div>
          </div>

          {/* Top Queries */}
          {gsc.topQueries && gsc.topQueries.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Top Search Queries</h4>
              <div className="overflow-hidden rounded-md border border-gray-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs">
                      <th className="text-left py-2 px-3 font-medium">Query</th>
                      <th className="text-right py-2 px-3 font-medium">Clicks</th>
                      <th className="text-right py-2 px-3 font-medium">Impressions</th>
                      <th className="text-right py-2 px-3 font-medium">CTR</th>
                      <th className="text-right py-2 px-3 font-medium">Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gsc.topQueries.slice(0, 10).map((q, i) => (
                      <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-800 font-medium">{q.keys?.[0] || q.query || ''}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{fmtNum(q.clicks)}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{fmtNum(q.impressions)}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{fmtPct(q.ctr)}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{fmtPos(q.position)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Pages */}
          {gsc.topPages && gsc.topPages.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Top Pages</h4>
              <div className="overflow-hidden rounded-md border border-gray-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs">
                      <th className="text-left py-2 px-3 font-medium">Page</th>
                      <th className="text-right py-2 px-3 font-medium">Clicks</th>
                      <th className="text-right py-2 px-3 font-medium">Impressions</th>
                      <th className="text-right py-2 px-3 font-medium">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gsc.topPages.slice(0, 5).map((p, i) => (
                      <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-800 font-medium truncate max-w-xs">{(p.keys?.[0] || p.page || '').replace(siteUrl || '', '/')}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{fmtNum(p.clicks)}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{fmtNum(p.impressions)}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{fmtPct(p.ctr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* GA4 Section */}
      {ga4 && ga4.current && (
        <div className="bg-white rounded-lg shadow-md p-5">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            <h3 className="text-sm font-semibold text-gray-700">Google Analytics</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Sessions */}
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Sessions</div>
              <div className="text-2xl font-bold text-orange-700">{fmtNum(ga4.current.sessions)}</div>
              <div className="flex items-center justify-between mt-1">
                <ChangeIndicator current={ga4.current.sessions} previous={ga4.previous?.sessions} />
                <MiniChart data={ga4.dailyTrend} dataKey="sessions" color="#F97316" />
              </div>
            </div>
            {/* Users */}
            <div className="bg-cyan-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Users</div>
              <div className="text-2xl font-bold text-cyan-700">{fmtNum(ga4.current.totalUsers)}</div>
              <div className="flex items-center justify-between mt-1">
                <ChangeIndicator current={ga4.current.totalUsers} previous={ga4.previous?.totalUsers} />
                <MiniChart data={ga4.dailyTrend} dataKey="users" color="#06B6D4" />
              </div>
            </div>
            {/* Page Views */}
            <div className="bg-indigo-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Page Views</div>
              <div className="text-2xl font-bold text-indigo-700">{fmtNum(ga4.current.screenPageViews)}</div>
              <div className="flex items-center justify-between mt-1">
                <ChangeIndicator current={ga4.current.screenPageViews} previous={ga4.previous?.screenPageViews} />
                <MiniChart data={ga4.dailyTrend} dataKey="pageviews" color="#6366F1" />
              </div>
            </div>
            {/* Bounce Rate */}
            <div className="bg-rose-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Bounce Rate</div>
              <div className="text-2xl font-bold text-rose-700">
                {ga4.current.bounceRate !== undefined ? (ga4.current.bounceRate * 100).toFixed(1) + '%' : ''}
              </div>
              <div className="flex items-center justify-between mt-1">
                {ga4.previous?.bounceRate !== undefined ? (
                  <span className={`text-xs font-medium ${ga4.current.bounceRate <= ga4.previous.bounceRate ? 'text-green-600' : 'text-red-500'}`}>
                    {ga4.current.bounceRate <= ga4.previous.bounceRate ? '' : ''} {Math.abs((ga4.current.bounceRate - ga4.previous.bounceRate) * 100).toFixed(1)}%
                  </span>
                ) : <span className="text-xs text-gray-400"></span>}
                <MiniChart data={ga4.dailyTrend} dataKey="bounceRate" color="#F43F5E" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleInsightsPanel;
